import React from 'react';
import { Refresh } from "@mui/icons-material";
import {
  Box,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';

import { object, string, date, array, TypeOf } from 'zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as crudgen from '../../../../share';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { HistoryType } from '../../../types/models/MergedHistory';
import { useGetCuttingPresentationQuery } from '../../../apis/Cutting.api';
import { useGetTimberStoragePresentationQuery } from '../../../apis/TimberStorage.api';
import { toString as toStringCutting } from '../../../types/models/Cutting';
import { toString as toStringTimberStorage } from '../../../types/models/TimberStorage';
import {
  emptyListPaginationResult,
  listPaginationLimits,
} from '../../../../types/global';
import { OrderBy } from '../../../../types/global.d';
import { formatDateTimeParam } from '../../../../utils/formatDate';
import { startOfWeek, endOfWeek } from 'date-fns';
import useSearchDebounce from '../../../../hooks/useSearchDebounce.ts';
import CuttingName, {
  CuttingNameDisplay,
} from '../../cutting/CuttingDetail/CuttingName.tsx';
import TimberStorageName, {
  TimberStorageNameDisplay,
} from '../../timberStorage/TimberStorageDetail/TimberStorageName.tsx';

export const formObject = {
  updatedAtFrom: date().nullish(),
  updatedAtTo: date().nullish(),
  action: string().trim().nullish(),
  historyTypes: string().array().nullish(),
  cuttingId: array(string()).nullish(),
  storageId: array(string()).nullish(),
};

export const formSchema = object(formObject);

export type FormInput = TypeOf<typeof formSchema>;

type IProps = {
  onFilterChange: (search: any) => void;
  onHistoryTypesChange: (types: HistoryType[]) => void;
  historyTypes: HistoryType[];
  refetch?: () => void;
};

export const HistoryListSearch = ({
  onFilterChange,
  onHistoryTypesChange,
  refetch,
}: IProps) => {
  const [localHistoryTypes, setLocalHistoryTypes] = React.useState<
    HistoryType[]
  >([]);

  const methods = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      updatedAtFrom: undefined,
      updatedAtTo: undefined,
      action: '',
      historyTypes: [],
      cuttingId: [],
      storageId: [],
    },
  });

  const { reset, setValue } = methods;

  React.useEffect(() => {
    setValue('updatedAtFrom', startOfWeek(new Date()));
    setValue('updatedAtTo', endOfWeek(new Date()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formData = useWatch({
    control: methods.control,
    name: ['cuttingId', 'storageId', 'action', 'updatedAtFrom', 'updatedAtTo'],
  });

  const [, setCuttingInputValue, cuttingInputTerm] = useSearchDebounce();
  const {
    data: cuttingList = emptyListPaginationResult,
    isLoading: getCuttingLoading,
    isFetching: getCuttingFetching,
  } = useGetCuttingPresentationQuery({
    list: listPaginationLimits,
    filter: {
      name: cuttingInputTerm,
      nameOr: false,
      orderBys: [] as OrderBy[],
      caseSensitive: false,
    },
  });

  const [, setStorageInputValue, storageInputTerm] = useSearchDebounce();
  const {
    data: storageList = emptyListPaginationResult,
    isLoading: getStorageLoading,
    isFetching: getStorageFetching,
  } = useGetTimberStoragePresentationQuery({
    list: listPaginationLimits,
    filter: {
      name: storageInputTerm,
      nameOr: false,
      cuttingIdRefs: formData[0] || [],
      orderBys: [] as OrderBy[],
      caseSensitive: false,
    },
  });

  const filterRef = React.useRef<any>({});

  const filter = React.useMemo(() => {
    const [cuttingId, storageId, action, updatedAtFrom, updatedAtTo] = formData;
    const f: any = {};
    if (action) {
      f.action = action;
    }
    if (updatedAtFrom) {
      f.updatedAtFrom = formatDateTimeParam(updatedAtFrom);
    }
    if (updatedAtTo) {
      f.updatedAtTo = formatDateTimeParam(updatedAtTo);
    }
    if (cuttingId && cuttingId.length > 0) {
      f.cuttingId = cuttingId[0];
    }
    if (storageId && storageId.length > 0) {
      f.storageId = storageId[0];
    }
    return f;
  }, [formData]);

  React.useEffect(() => {
    if (JSON.stringify(filter) !== JSON.stringify(filterRef.current)) {
      filterRef.current = filter;
      onFilterChange(filter);
    }
  }, [filter, onFilterChange]);

  React.useEffect(() => {
    onHistoryTypesChange(localHistoryTypes);
  }, [localHistoryTypes, onHistoryTypesChange]);

  const handleHistoryTypesChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTypes: HistoryType[],
  ) => {
    setLocalHistoryTypes(newTypes);
  };

  const cancelSearch = () => {
    reset();
    setLocalHistoryTypes([]);
    onFilterChange({});
    onHistoryTypesChange([]);
  };

  return (
    <Container maxWidth={false}>
      <FormProvider {...methods}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
          data-testid="History-search-form"
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <ToggleButtonGroup
              value={localHistoryTypes}
              onChange={handleHistoryTypesChange}
              size="small"
              data-testid="History-filter-historyTypes"
            >
              <ToggleButton
                value="Shipment"
                data-testid="History-filter-historyTypes-Shipment"
              >
                <Trans>Shipments</Trans>
              </ToggleButton>
              <ToggleButton
                value="Log"
                data-testid="History-filter-historyTypes-Log"
              >
                <Trans>Logs</Trans>
              </ToggleButton>
              <ToggleButton
                value="Cordwood"
                data-testid="History-filter-historyTypes-Cordwood"
              >
                <Trans>Cordwood</Trans>
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControl
              size="small"
              sx={{ minWidth: 150 }}
              data-testid="History-filter-action-form"
            >
              <InputLabel id="action-select-label">
                <Trans>Action</Trans>
              </InputLabel>
              <Select
                labelId="action-select-label"
                id="action-select"
                label={t`Action`}
                data-testid="History-filter-action"
                {...methods.register('action')}
                defaultValue=""
              >
                <MenuItem value="">
                  <Trans>All</Trans>
                </MenuItem>
                <MenuItem value="create">
                  <Trans>Created</Trans>
                </MenuItem>
                <MenuItem value="update">
                  <Trans>Updated</Trans>
                </MenuItem>
                <MenuItem value="delete">
                  <Trans>Deleted</Trans>
                </MenuItem>
                <MenuItem value="shipment/cancel">
                  <Trans>Cancelled from Shipment</Trans>
                </MenuItem>
              </Select>
            </FormControl>

            <Box data-testid="History-filter-cuttingId-wrapper">
              <crudgen.AsyncFormSelect
                name="cuttingId"
                label={t`Cutting`}
                multiple
                options={(cuttingList.items || []) as any}
                optionsLoading={getCuttingLoading || getCuttingFetching}
                optionLabelMap={toStringCutting as any}
                onInputChange={setCuttingInputValue}
                renderOption={(optionProps: any, option: any) => {
                  const { key, ...filteredOptionProps } = optionProps;
                  return (
                    <li key={key} {...filteredOptionProps}>
                      <CuttingNameDisplay data={option} />
                    </li>
                  );
                }}
                renderValue={(value, getItemProps) =>
                  value.map((option: any, index: number) => {
                    const { key: _key, ...tagProps } = getItemProps({ index });
                    return (
                      <crudgen.SelectChip key={index} {...tagProps}>
                        <CuttingName id={option} />
                      </crudgen.SelectChip>
                    );
                  })
                }
              />
            </Box>

            <Box data-testid="History-filter-storageId-wrapper">
              <crudgen.AsyncFormSelect
                name="storageId"
                label={t`Timber Storage`}
                multiple
                options={(storageList.items || []) as any}
                optionsLoading={getStorageLoading || getStorageFetching}
                optionLabelMap={toStringTimberStorage as any}
                onInputChange={setStorageInputValue}
                renderOption={(optionProps: any, option: any) => {
                  const { key, ...filteredOptionProps } = optionProps;
                  return (
                    <li key={key} {...filteredOptionProps}>
                      <TimberStorageNameDisplay data={option} />
                    </li>
                  );
                }}
                renderValue={(value, getItemProps) =>
                  value.map((option: any, index: number) => {
                    const { key: _key, ...tagProps } = getItemProps({ index });
                    return (
                      <crudgen.SelectChip key={index} {...tagProps}>
                        <TimberStorageName id={option} />
                      </crudgen.SelectChip>
                    );
                  })
                }
              />
            </Box>

            <Box data-testid="History-filter-dateRange-wrapper">
              <crudgen.FormDateRange
                startName="updatedAtFrom"
                endName="updatedAtTo"
                startLabel={t`Updated From`}
                endLabel={t`Updated To`}
                initialDateRange={{
                  startDate: startOfWeek(new Date()),
                  endDate: endOfWeek(new Date()),
                }}
              />
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            <Box sx={{ flexGrow: 1 }} />
            <ToggleButton
              size="small"
              value="refresh"
              onClick={() => refetch?.()}
              data-testid="History-search-refresh"
            >
              <Refresh />
            </ToggleButton>
            <ToggleButton
              size="small"
              value="clear"
              onClick={cancelSearch}
              data-testid="History-search-clear"
            >
              <Trans>Clear</Trans>
            </ToggleButton>
          </Stack>
        </Box>
      </FormProvider>
    </Container>
  );
};

export default HistoryListSearch;
